// Story 11.6 — CopyButton primitive.
// Encapsulates clipboard copy state machine (idle/copied/failed), auto-revert
// timers, lucide icon morph (Copy ↔ Check), lime flash via CSS keyframe,
// optional GSAP energy sweep on the label, and an aria-live announcement for
// screen readers. Dispatches to a static branch under `prefers-reduced-motion`
// so the reduced-motion render never imports gsap.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type JSX,
} from 'react';
import { Copy, Check } from 'lucide-react';
import { loadGsap, prefersReducedMotion } from '../../../lib/gsap-setup.js';
import { cn } from '../../../lib/cn.js';

export type CopyButtonVariant = 'ghost' | 'primary' | 'inline';

type CopyState = 'idle' | 'copied' | 'failed';

type ForwardedAttrs = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' | 'children' | 'aria-label'
>;

export interface CopyButtonProps extends ForwardedAttrs {
  /** Text written to the clipboard on click. */
  text: string;
  /** Idle-state label. Default 'Copy'. */
  label?: string;
  /** Copied-state label. Default 'Copied!'. */
  copiedLabel?: string;
  /** Failure-state label. Default 'Copy failed — select manually'. */
  failedLabel?: string;
  /** Milliseconds before reverting from copied → idle. Default 2000. */
  revertMs?: number;
  /** Milliseconds before reverting from failed → idle. Default 3000. */
  failedRevertMs?: number;
  /** Variant sentinel — forwarded as `data-variant` for the `.btn-press` CSS ghost exception. */
  variant?: CopyButtonVariant;
  /** Whether to render a leading clipboard/check icon. Default 'clipboard'. */
  icon?: 'clipboard' | 'none';
  /** Screen-reader announcement override. Defaults to the copiedLabel. */
  'aria-label'?: string;
  /** Test identifier forwarded to the rendered button. */
  'data-testid'?: string;
}

const DEFAULT_LABEL = 'Copy';
const DEFAULT_COPIED = 'Copied!';
const DEFAULT_FAILED = 'Copy failed — select manually';

function useCopyState(
  text: string,
  revertMs: number,
  failedRevertMs: number,
): { state: CopyState; handleCopy: () => void } {
  const [state, setState] = useState<CopyState>('idle');
  const revertTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
    };
  }, []);

  const handleCopy = useCallback(() => {
    const writePromise = navigator?.clipboard?.writeText
      ? navigator.clipboard.writeText(text)
      : Promise.reject(new Error('clipboard unavailable'));

    void writePromise
      .then(() => {
        if (!mountedRef.current) return;
        setState('copied');
        if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
        revertTimerRef.current = setTimeout(() => {
          if (mountedRef.current) setState('idle');
        }, revertMs);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setState('failed');
        if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
        revertTimerRef.current = setTimeout(() => {
          if (mountedRef.current) setState('idle');
        }, failedRevertMs);
      });
  }, [text, revertMs, failedRevertMs]);

  return { state, handleCopy };
}

function resolveLabel(
  state: CopyState,
  label: string,
  copiedLabel: string,
  failedLabel: string,
): string {
  if (state === 'copied') return copiedLabel;
  if (state === 'failed') return failedLabel;
  return label;
}

function renderIcon(state: CopyState, iconMode: 'clipboard' | 'none'): JSX.Element | null {
  if (iconMode === 'none') return null;
  if (state === 'copied') {
    return <Check className="w-4 h-4" aria-hidden="true" />;
  }
  return <Copy className="w-4 h-4" aria-hidden="true" />;
}

function CopyButtonStatic(props: CopyButtonProps): JSX.Element {
  const {
    text,
    label = DEFAULT_LABEL,
    copiedLabel = DEFAULT_COPIED,
    failedLabel = DEFAULT_FAILED,
    revertMs = 2000,
    failedRevertMs = 3000,
    variant,
    icon = 'clipboard',
    className,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    type = 'button',
    ...rest
  } = props;
  const { state, handleCopy } = useCopyState(text, revertMs, failedRevertMs);
  const displayLabel = resolveLabel(state, label, copiedLabel, failedLabel);
  return (
    <>
      <button
        {...rest}
        type={type}
        data-variant={variant}
        data-copy-state={state}
        data-testid={dataTestId ?? 'copy-button'}
        aria-label={ariaLabel}
        onClick={handleCopy}
        className={cn(
          'btn-press inline-flex items-center gap-1.5 rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
          className,
        )}
      >
        {renderIcon(state, icon)}
        <span>{displayLabel}</span>
      </button>
      <span className="sr-only" aria-live="polite">
        {state === 'copied' ? copiedLabel : state === 'failed' ? failedLabel : ''}
      </span>
    </>
  );
}

function CopyButtonMotion(props: CopyButtonProps): JSX.Element {
  const {
    text,
    label = DEFAULT_LABEL,
    copiedLabel = DEFAULT_COPIED,
    failedLabel = DEFAULT_FAILED,
    revertMs = 2000,
    failedRevertMs = 3000,
    variant,
    icon = 'clipboard',
    className,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    type = 'button',
    ...rest
  } = props;
  const { state, handleCopy } = useCopyState(text, revertMs, failedRevertMs);
  const labelRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (state !== 'copied') return;
    const el = labelRef.current;
    if (!el) return;
    let cancelled = false;
    void loadGsap()
      .then((gsap) => {
        if (cancelled) return;
        // Lightsaber-style one-shot energy sweep: single bright pass that
        // settles back to filter:none via clearProps. ~400ms total.
        gsap.fromTo(
          el,
          { filter: 'brightness(1)' },
          {
            filter: 'brightness(1.6)',
            duration: 0.2,
            yoyo: true,
            repeat: 1,
            ease: 'power2.inOut',
            clearProps: 'filter',
          },
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [state]);

  const displayLabel = resolveLabel(state, label, copiedLabel, failedLabel);
  return (
    <>
      <button
        {...rest}
        type={type}
        data-variant={variant}
        data-copy-state={state}
        data-testid={dataTestId ?? 'copy-button'}
        aria-label={ariaLabel}
        onClick={handleCopy}
        className={cn(
          'btn-press inline-flex items-center gap-1.5 rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
          className,
        )}
      >
        {renderIcon(state, icon)}
        <span ref={labelRef}>{displayLabel}</span>
      </button>
      <span className="sr-only" aria-live="polite">
        {state === 'copied' ? copiedLabel : state === 'failed' ? failedLabel : ''}
      </span>
    </>
  );
}

export function CopyButton(props: CopyButtonProps): JSX.Element {
  if (prefersReducedMotion) return <CopyButtonStatic {...props} />;
  return <CopyButtonMotion {...props} />;
}
